import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  authUpdateProfile,
  createAthleteProfile,
  getAthleteProfile,
  patchAthleteProfile,
  stravaConfig,
  stravaConnect,
  stravaDisconnect,
  stravaStatus,
  stravaSync,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { toKg, weightValue } from "../lib/format";
import { buildProfileUpdate } from "../lib/profileUpdate";
import { readOnboardingDraft } from "../lib/onboarding";
import { toast } from "sonner";
import { Loader2, Unlink, RefreshCw, User as UserIcon } from "lucide-react";
import { StravaConnectButton } from "../components/StravaConnectButton";
import { CoachingAutonomySettings } from "../components/coaching/CoachingAutonomySettings";
import { CoachingMemorySettings } from "../components/coaching/CoachingMemorySettings";
import {
  athleteStravaView,
  canStravaSync,
  formatStravaHistory,
  formatStravaLastSync,
  isStravaAutoSyncActive,
  shouldShowStravaConnect,
  stravaOauthErrorMessage,
} from "../lib/stravaStatus";
import { PageHeader } from "../components/PageHeader";
import { SectionHeader } from "../components/layout/SectionHeader";
import { SportBadge } from "../components/SportBadge";
import { TimezoneSelect } from "../components/TimezoneSelect";
import { canonicalizeIana, formatPrimaryLabel } from "../lib/timezones";
import { formatSport } from "../lib/coachingFormatters";
import { cn } from "@/lib/utils";

const TRAINING_SPORT_BADGE_SPORTS = new Set(["cycling", "running", "swimming"]);

const formatTrainingSportLabel = (sport) => {
  if (!sport) return null;
  const key = String(sport).toLowerCase();
  if (key === "duathlon") return "Duathlon";
  return formatSport(sport);
};

const coachLinkCls =
  "inline-block text-sm text-brand-primary hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50";

const Field = ({ label, unit, hint, children }) => (
  <label className="block min-w-0">
    <div className="card-label mb-2">
      {label}
      {unit ? <span className="normal-case tracking-normal font-normal"> ({unit})</span> : null}
    </div>
    {hint ? <p className="meta-text mb-2">{hint}</p> : null}
    {children}
  </label>
);

const SettingsSection = ({ title, description, children, testId, className }) => (
  <section className={cn("app-card p-5 sm:p-6 min-w-0", className)} data-testid={testId}>
    <SectionHeader title={title} description={description} />
    {children}
  </section>
);

const MetaBlock = ({ label, children, testId }) => (
  <div className="app-card-elevated p-3 min-w-0" data-testid={testId}>
    <div className="card-label">{label}</div>
    <div className="mt-1 text-sm text-dark-text-primary min-w-0 break-words">{children}</div>
  </div>
);

const inputCls = "app-control font-mono text-sm";

const saveBtnCls =
  "btn-primary-app px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-md min-h-[44px] inline-flex items-center gap-2 disabled:opacity-50";

function syncErrorMessage(detail) {
  if (detail && typeof detail === "object") {
    if (detail.code === "token_revoked") return "Strava connection expired. Reconnect Strava.";
    if (detail.code === "strava_rate_limited") return "Sync paused — will resume automatically";
    if (typeof detail.message === "string" && detail.message && !/token|secret|cipher/i.test(detail.message)) {
      return detail.message;
    }
  }
  return "Sync could not start. Try again.";
}

function stravaStatusBadgeClass(viewState) {
  if (viewState === "CONNECTED") {
    return "border-analytics-green text-analytics-green bg-analytics-green/10";
  }
  if (viewState === "IMPORTING" || viewState === "PAUSED" || viewState === "RECONNECT" || viewState === "ERROR") {
    return "border-analytics-amber text-analytics-amber bg-analytics-amber/10";
  }
  return "border-[var(--border-primary)] text-dark-text-secondary bg-[var(--surface-secondary)]";
}

/** Friendly names for backend ProfileUpdate validation errors. */
const PROFILE_FIELD_LABELS = {
  max_hr: "Max HR",
  ftp: "FTP",
  weight_kg: "Weight",
  resting_hr: "Resting HR",
  height_cm: "Height",
  years_experience: "Years of experience",
  gender: "Gender",
  dob: "Date of birth",
  experience_level: "Experience level",
  bike_type: "Bike type",
  has_power_meter: "Power meter",
  unit_system: "Units",
  temperature_unit: "Temperature",
  default_sport: "Preferred sport",
};

const PROFILE_FIELD_HINTS = {
  max_hr: "Max HR must be between 100 and 230 bpm (this is maximum HR, not average).",
  ftp: "FTP must be between 50 and 600 watts.",
  weight_kg: "Weight must be between 20 and 250 kg.",
  resting_hr: "Resting HR must be between 30 and 120 bpm (not average workout HR).",
  height_cm: "Height must be between 100 and 250 cm.",
  years_experience: "Years of experience must be between 0 and 80.",
};

function formatProfileUpdateError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message.trim();
    }
  }
  if (Array.isArray(detail) && detail.length) {
    const parts = detail.map((item) => {
      const field = Array.isArray(item?.loc) ? item.loc.filter((p) => p !== "body").join(".") : "";
      const label = PROFILE_FIELD_LABELS[field] || field || "Field";
      if (PROFILE_FIELD_HINTS[field]) return PROFILE_FIELD_HINTS[field];
      const msg = typeof item?.msg === "string" ? item.msg : "invalid value";
      return `${label}: ${msg}`;
    });
    return parts.join(" ");
  }
  if (error?.response?.status === 422) {
    return "One or more profile values are outside the allowed range. Check Max HR (100–230), Resting HR (30–120), FTP (50–600), and Weight.";
  }
  return "Update failed";
}

function isStoredAthleteProfile(profile) {
  const revision = Number(profile?.revision);
  return profile?.persistence_status === "stored" && Number.isFinite(revision) && revision >= 1;
}

function isProfileAlreadyExistsError(error) {
  if (error?.response?.status !== 409) return false;
  const detail = error?.response?.data?.detail;
  if (typeof detail === "object" && detail?.error === "profile_already_exists") return true;
  return typeof detail === "string" && /profile_already_exists/i.test(detail);
}

export const Settings = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const [form, setForm] = useState({
    max_hr: user?.max_hr || "",
    ftp: user?.ftp || "",
    weight_kg: user?.weight_kg || "",
    resting_hr: user?.resting_hr || "",
    gender: user?.gender || "",
    dob: user?.dob || "",
    height_cm: user?.height_cm || "",
    experience_level: user?.experience_level || "",
    years_experience: user?.years_experience ?? "",
    bike_type: user?.bike_type || "",
    has_power_meter:
      user?.has_power_meter === true ? "yes" : user?.has_power_meter === false ? "no" : "",
    unit_system: user?.unit_system || "metric",
    temperature_unit: user?.temperature_unit || "celsius",
    default_sport: user?.default_sport || "cycling",
  });
  const [saving, setSaving] = useState(false);
  const [strava, setStrava] = useState({ configured: false });
  const [tzProfile, setTzProfile] = useState(null); // {timezone, revision, persistence_status}
  const [tzInput, setTzInput] = useState("");
  const [tzSaving, setTzSaving] = useState(false);
  const detectedTz = (() => {
    try {
      const raw = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      return canonicalizeIana(raw) || raw;
    } catch {
      return "";
    }
  })();
  const [stravaStatusData, setStravaStatusData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const actionLock = useRef(false);
  const unitSystem = form.unit_system || "metric";
  const weightUnit = unitSystem === "imperial" ? "lb" : "kg";
  const weightDisplay = (() => {
    if (form.weight_kg === "" || form.weight_kg == null) return "";
    const kg = Number(form.weight_kg);
    if (!Number.isFinite(kg)) return "";
    const value = weightValue(kg, unitSystem);
    return Number.isFinite(value) ? value.toFixed(1) : "";
  })();

  useEffect(() => {
    let cancelled = false;
    getAthleteProfile()
      .then((p) => {
        if (cancelled) return;
        setTzProfile(p || null);
        setTzInput((p && p.timezone) || "");
      })
      .catch(() => {
        if (cancelled) return;
        setTzProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveTimezone = async () => {
    const value = canonicalizeIana((tzInput || "").trim());
    if (!value) {
      toast.error("Select a timezone from the list.");
      return;
    }
    setTzSaving(true);
    try {
      let updated;
      if (isStoredAthleteProfile(tzProfile)) {
        updated = await patchAthleteProfile({
          expected_revision: tzProfile.revision,
          timezone: value,
        });
      } else {
        try {
          updated = await createAthleteProfile({ timezone: value });
        } catch (error) {
          if (!isProfileAlreadyExistsError(error)) throw error;
          const latest = await getAthleteProfile();
          if (!isStoredAthleteProfile(latest)) throw error;
          updated = await patchAthleteProfile({
            expected_revision: latest.revision,
            timezone: value,
          });
        }
      }
      setTzProfile(updated);
      setTzInput(updated?.timezone || value);
      toast.success("Timezone saved");
    } catch (error) {
      toast.error(formatProfileUpdateError(error));
    } finally {
      setTzSaving(false);
    }
  };

  useEffect(() => {
    stravaConfig().then(setStrava).catch(() => {});
    stravaStatus()
      .then(setStravaStatusData)
      .catch(() => setStravaStatusData(null));
  }, [user?.strava_connected]);

  useEffect(() => {
    if (!isStravaAutoSyncActive(stravaStatusData)) return undefined;
    const timer = window.setInterval(() => {
      stravaStatus()
        .then(setStravaStatusData)
        .catch(() => {});
    }, 30000);
    return () => window.clearInterval(timer);
    // Poll only while auto-sync is active; re-arm when exhaustion flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stravaStatusData?.auto_sync_active,
    stravaStatusData?.connected,
    stravaStatusData?.historical_sync_exhausted,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const connected = params.get("strava_connected") === "1";
    const err = params.get("strava_error");
    if (!connected && !err) return;
    if (!user) return;
    const resumeSetup = Boolean(user.user_id && readOnboardingDraft(user.user_id));
    if (connected) {
      toast.success("Strava connected");
      refresh();
      stravaStatus().then(setStravaStatusData).catch(() => {});
      nav(resumeSetup ? "/" : "/settings", { replace: true });
    }
    if (err) {
      toast.error(stravaOauthErrorMessage(err));
      nav(resumeSetup ? "/" : "/settings", { replace: true });
    }
  }, [loc.search, nav, refresh, user]);

  useEffect(() => {
    if (!user) return;
    setForm({
      max_hr: user.max_hr || "",
      ftp: user.ftp || "",
      weight_kg: user.weight_kg || "",
      resting_hr: user.resting_hr || "",
      gender: user.gender || "",
      dob: user.dob || "",
      height_cm: user.height_cm || "",
      experience_level: user.experience_level || "",
      years_experience: user.years_experience ?? "",
      bike_type: user.bike_type || "",
      has_power_meter:
        user.has_power_meter === true ? "yes" : user.has_power_meter === false ? "no" : "",
      unit_system: user.unit_system || "metric",
      temperature_unit: user.temperature_unit || "celsius",
      default_sport: user.default_sport || "cycling",
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const built = buildProfileUpdate(form);
      if (built.error) {
        toast.error(built.error);
        return;
      }
      await authUpdateProfile(built.payload);
      toast.success("Profile updated");
      refresh();
    } catch (error) {
      toast.error(formatProfileUpdateError(error));
    } finally {
      setSaving(false);
    }
  };

  const connectStrava = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    setConnecting(true);
    try {
      const { url } = await stravaConnect();
      window.location.href = url;
    } catch {
      toast.error("Could not start Strava connection");
      actionLock.current = false;
      setConnecting(false);
    }
  };

  const disconnectStrava = async () => {
    if (actionLock.current) return;
    if (!window.confirm(
      "Disconnect Strava? Future automatic sync will stop. Activities already imported into Endurava will stay.",
    )) {
      return;
    }
    actionLock.current = true;
    setDisconnecting(true);
    try {
      await stravaDisconnect();
      toast.success("Strava disconnected. Imported activities stay in Endurava.");
      refresh();
      setStravaStatusData({ connected: false, status: "disconnected" });
    } catch {
      toast.error("Could not disconnect Strava. Try again.");
    } finally {
      actionLock.current = false;
      setDisconnecting(false);
    }
  };

  const doSync = async () => {
    if (actionLock.current) {
      toast.message("Sync already in progress");
      return;
    }
    if (!canStravaSync(stravaStatusData, { syncing })) {
      return;
    }
    actionLock.current = true;
    setSyncing(true);
    try {
      await stravaSync();
      toast.success("Sync requested");
      const status = await stravaStatus();
      setStravaStatusData(status);
    } catch (e) {
      toast.error(syncErrorMessage(e?.response?.data?.detail));
    } finally {
      actionLock.current = false;
      setSyncing(false);
    }
  };

  const experienceLabel = form.experience_level
    ? form.experience_level.charAt(0).toUpperCase() + form.experience_level.slice(1)
    : null;
  const storedPreferredSport = user?.default_sport || null;
  const trainingSportRaw = tzProfile?.primary_sport;
  const trainingSportKey = trainingSportRaw
    ? String(trainingSportRaw).toLowerCase()
    : null;
  const trainingSportLabel = formatTrainingSportLabel(trainingSportRaw);
  const stravaView = athleteStravaView(stravaStatusData);
  const stravaHistory = formatStravaHistory(stravaStatusData);
  const stravaLastSync = formatStravaLastSync(stravaStatusData?.last_successful_sync_at);
  const stravaUsable = ["CONNECTED", "IMPORTING", "PAUSED", "ERROR"].includes(stravaView.state);
  const showTrainingSportBadge =
    trainingSportKey && TRAINING_SPORT_BADGE_SPORTS.has(trainingSportKey);

  return (
    <div className="max-w-6xl mx-auto min-w-0">
      <PageHeader
        eyebrow="Account"
        title="Settings"
        description="Set your athlete profile, zone parameters, and display preferences."
      />

      {/* Profile identity */}
      <section
        className="app-card p-5 sm:p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
        data-testid="profile-identity-card"
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-16 h-16 rounded-full border border-[var(--border-primary)] object-cover shrink-0"
          />
        ) : (
          <div
            className="w-16 h-16 rounded-full border border-[var(--border-primary)] bg-[var(--surface-secondary)] flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <UserIcon className="w-7 h-7 text-dark-text-muted" strokeWidth={1.5} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold text-dark-text-primary truncate">{user?.name}</div>
          <div className="text-sm text-dark-text-muted font-mono truncate mt-0.5">{user?.email}</div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {storedPreferredSport ? (
              <SportBadge sport={storedPreferredSport} />
            ) : null}
            {experienceLabel ? (
              <span className="text-xs text-dark-text-secondary meta-text">{experienceLabel}</span>
            ) : null}
          </div>
        </div>
      </section>

      <div
        className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
        data-testid="settings-columns"
      >
        <div className="flex flex-col gap-6 min-w-0" data-testid="settings-column-left">
          <SettingsSection
            className="min-w-0 w-full"
            title="Athlete Profile"
            description="General information about you as an athlete."
            testId="athlete-details-form"
          >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Height" unit="cm">
                  <input
                    type="number"
                    step="0.1"
                    className={inputCls}
                    value={form.height_cm}
                    onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                    data-testid="input-height"
                    placeholder="e.g. 175"
                  />
                </Field>
                <Field label="Weight" unit={weightUnit}>
                  <input
                    type="number"
                    step="0.1"
                    className={inputCls}
                    value={weightDisplay}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setForm({ ...form, weight_kg: "" });
                        return;
                      }
                      const num = Number(raw);
                      if (!Number.isFinite(num)) return;
                      const kg = toKg(num, unitSystem);
                      setForm({ ...form, weight_kg: kg });
                    }}
                    data-testid="input-weight"
                    placeholder={unitSystem === "imperial" ? "e.g. 155" : "e.g. 70"}
                  />
                </Field>
                <Field label="Gender">
                  <select
                    className={inputCls}
                    value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    data-testid="input-gender"
                  >
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Date of Birth">
                  <input
                    type="date"
                    className={inputCls}
                    value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    data-testid="input-dob"
                  />
                </Field>
                <Field label="Experience Level">
                  <select
                    className={inputCls}
                    value={form.experience_level}
                    onChange={(e) => setForm({ ...form, experience_level: e.target.value })}
                    data-testid="input-experience-level"
                  >
                    <option value="">—</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                  </select>
                </Field>
                <Field
                  label="Years of Experience"
                  hint="Total years training in your primary endurance sports."
                >
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={form.years_experience}
                    onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                    data-testid="input-years-experience"
                    placeholder="e.g. 5"
                  />
                </Field>
            </div>
          </SettingsSection>

          <SettingsSection
            title="Sport & Equipment"
            description="Your sport preferences and sport-specific equipment settings."
            testId="sport-equipment-form"
          >
            <div className="space-y-5">
              <Field label="Preferred Sport">
                <div className="space-y-2">
                  <select
                    className={inputCls}
                    value={form.default_sport}
                    onChange={(e) => setForm({ ...form, default_sport: e.target.value })}
                    data-testid="input-default-sport"
                  >
                    <option value="cycling">Cycling</option>
                    <option value="running">Running</option>
                    <option value="swimming">Swimming</option>
                    <option value="walking">Walking</option>
                    <option value="hiking">Hiking</option>
                    <option value="other">Other</option>
                  </select>
                  <p className="meta-text text-dark-text-secondary">
                    Your general sport preference for your profile. Training plans and AI Coach use
                    the sport you choose in AI Coach, not this setting.
                  </p>
                </div>
              </Field>

              <p className="meta-text text-dark-text-secondary">
                Walking and hiking are supported for activity tracking. Structured training plans
                are currently available for cycling, running, swimming, walking, triathlon, and duathlon in AI Coach.
              </p>

              <div className="space-y-2" data-testid="training-sport-display">
                <div className="card-label" id="settings-training-sport-label">
                  Training Sport
                </div>
                <div
                  className="app-card-elevated p-3 space-y-2 min-w-0"
                  aria-labelledby="settings-training-sport-label"
                  role="group"
                >
                  {trainingSportKey ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        {showTrainingSportBadge ? (
                          <SportBadge sport={trainingSportKey} />
                        ) : null}
                        <span className="text-sm text-dark-text-primary">{trainingSportLabel}</span>
                      </div>
                      <Link to="/coach" className={coachLinkCls} data-testid="training-sport-coach-link">
                        Change in AI Coach
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-dark-text-primary">No training sport selected</p>
                      <p className="meta-text text-dark-text-secondary">
                        Set up a training goal in AI Coach to choose your training sport.
                      </p>
                      <Link to="/coach" className={coachLinkCls} data-testid="training-sport-coach-link">
                        Set up in AI Coach
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-[var(--border-primary)]">
              <div className="flex items-center gap-2 mb-4">
                <SportBadge sport="cycling" />
                <span className="card-label">Cycling equipment</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Bike Type">
                  <select
                    className={inputCls}
                    value={form.bike_type}
                    onChange={(e) => setForm({ ...form, bike_type: e.target.value })}
                    data-testid="input-bike-type"
                  >
                    <option value="">—</option>
                    <option value="road">Road</option>
                    <option value="mtb">MTB</option>
                    <option value="tt">TT</option>
                    <option value="gravel">Gravel</option>
                  </select>
                </Field>
                <Field label="Power Meter">
                  <select
                    className={inputCls}
                    value={form.has_power_meter}
                    onChange={(e) => setForm({ ...form, has_power_meter: e.target.value })}
                    data-testid="input-power-meter"
                  >
                    <option value="">—</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </Field>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection
            title="Units & Preferences"
            description="How distances, weight, and temperature are displayed across Endurava."
            testId="preferences-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Units & Measurements">
                <select
                  className={inputCls}
                  value={form.unit_system}
                  onChange={(e) => setForm({ ...form, unit_system: e.target.value })}
                  data-testid="input-unit-system"
                >
                  <option value="metric">Metric (kg / km)</option>
                  <option value="imperial">Imperial (lb / mi)</option>
                </select>
              </Field>
              <Field label="Temperature">
                <select
                  className={inputCls}
                  value={form.temperature_unit}
                  onChange={(e) => setForm({ ...form, temperature_unit: e.target.value })}
                  data-testid="input-temperature-unit"
                >
                  <option value="celsius">Celsius (°C)</option>
                  <option value="fahrenheit">Fahrenheit (°F)</option>
                </select>
              </Field>
            </div>

            <div className="mt-6 pt-5 border-t border-[var(--border-primary)]">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className={saveBtnCls}
                data-testid="save-profile-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
                Save Profile
              </button>
            </div>
          </SettingsSection>
        </div>

        <div className="flex flex-col gap-6 min-w-0" data-testid="settings-column-right">
          <section
            className="app-card p-5 sm:p-6 min-w-0 w-full overflow-x-hidden"
            data-testid="strava-section"
          >
            <SectionHeader
              title="Connected Services"
              description="Link external accounts to import and sync your training data."
            />

            <div className="app-card-elevated p-4 min-w-0" data-testid="strava-integration-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-heading text-xl tracking-wider uppercase text-[#FC5200]">Strava</h3>
                  <p
                    className="text-xs text-dark-text-secondary mt-2 break-words"
                    data-testid="strava-scope-summary"
                  >
                    {strava.scope_explanation?.summary ||
                      "Import activities from Strava including distance, time, GPS, heart rate, and power where recorded."}
                  </p>
                </div>
                {strava.configured && stravaView.state !== "LOADING" ? (
                  <span
                    className={cn(
                      "inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border rounded-full shrink-0 max-w-full break-words",
                      stravaStatusBadgeClass(stravaView.state)
                    )}
                    data-testid="strava-status-badge"
                  >
                    {stravaView.label}
                  </span>
                ) : null}
              </div>

              <p className="text-[11px] text-dark-text-muted mt-3 break-words" data-testid="strava-privacy-copy">
                Visibility changes may remove access to activities Endurava can no longer read. You can disconnect
                anytime to revoke Endurava access in Strava.
              </p>

              {stravaView.state === "RECONNECT" ? (
                <p data-testid="strava-reconnect-message" className="text-xs text-dark-text-secondary mt-4 break-words">
                  {stravaView.message}
                </p>
              ) : null}
              {stravaView.state === "NOT_CONNECTED" && strava.configured ? (
                <p data-testid="strava-not-connected" className="text-xs text-dark-text-secondary mt-4">
                  Not connected
                </p>
              ) : null}
              {stravaUsable ? (
                <div
                  className="mt-4 pt-4 border-t border-[var(--border-primary)] space-y-3 text-xs min-w-0"
                  data-testid="strava-connected-details"
                >
                  {stravaStatusData.athlete_display_name ? (
                    <div data-testid="strava-athlete-name" className="min-w-0">
                      <span className="card-label">Account</span>
                      <div className="text-dark-text-primary mt-1 break-words">{stravaStatusData.athlete_display_name}</div>
                    </div>
                  ) : null}
                  <div data-testid="strava-status-label" className="min-w-0">
                    <span className="card-label">Status</span>
                    <div className="text-dark-text-primary mt-1 break-words">{stravaView.label}</div>
                    {stravaHistory ? (
                      <div
                        className="mt-2 rounded-md border border-brand-primary/30 bg-brand-primary/10 px-3 py-2 text-[11px] text-dark-text-secondary break-words"
                        data-testid="strava-auto-sync-progress"
                      >
                        {stravaHistory}
                      </div>
                    ) : null}
                    {stravaView.message ? (
                      <div className="text-dark-text-muted mt-1 break-words" data-testid="strava-status-hint">
                        {stravaView.message}
                      </div>
                    ) : null}
                  </div>
                  {stravaLastSync ? (
                    <div data-testid="strava-last-sync" className="min-w-0">
                      <span className="card-label">Last sync</span>
                      <div className="text-dark-text-primary mt-1 break-words">{stravaLastSync}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-4 flex flex-col gap-2">
                {!strava.configured ? (
                  <div
                    className="text-[11px] text-dark-text-muted border border-[var(--border-primary)] bg-[var(--surface-secondary)] px-3 py-2 rounded-md break-words"
                    data-testid="strava-disabled-message"
                  >
                    Strava integration is disabled on this server. Contact your administrator to enable it.
                  </div>
                ) : shouldShowStravaConnect(strava.configured, stravaStatusData, user?.strava_connected) ? (
                  <StravaConnectButton onClick={connectStrava} connecting={connecting} disabled={connecting} />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={doSync}
                      disabled={!canStravaSync(stravaStatusData, { syncing })}
                      className="w-full inline-flex items-center justify-center gap-2 border border-[#FC5200]/60 bg-[#FC5200]/10 hover:bg-[#FC5200]/20 text-[#FC5200] transition-colors px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold rounded-md disabled:opacity-50"
                      data-testid="strava-sync-button"
                      aria-label="Sync Strava"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <RefreshCw className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                      )}
                      Sync
                    </button>
                    {stravaView.state === "CONNECTED" || stravaView.state === "IMPORTING" ? (
                      <p className="text-[10px] text-dark-text-muted text-center break-words">
                        Optional — new rides also sync automatically.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={disconnectStrava}
                      disabled={disconnecting}
                      className="btn-danger-app w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="strava-disconnect-button"
                      aria-label="Disconnect Strava"
                    >
                      <Unlink className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>

          <SettingsSection
            title="Performance Parameters"
            description="Physiology and sport-specific performance values used for zones and analysis."
            testId="profile-form"
          >
            <div className="mb-5">
              <h3 className="card-label mb-4">General physiology</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Max HR" unit="bpm">
                  <input
                    type="number"
                    min={100}
                    max={230}
                    className={inputCls}
                    value={form.max_hr}
                    onChange={(e) => setForm({ ...form, max_hr: e.target.value })}
                    data-testid="input-max-hr"
                    placeholder="e.g. 190"
                  />
                </Field>
                <Field label="Resting HR" unit="bpm">
                  <input
                    type="number"
                    min={30}
                    max={120}
                    className={inputCls}
                    value={form.resting_hr}
                    onChange={(e) => setForm({ ...form, resting_hr: e.target.value })}
                    data-testid="input-resting-hr"
                    placeholder="e.g. 55"
                  />
                </Field>
              </div>
            </div>

            <div className="pt-5 border-t border-[var(--border-primary)]">
              <div className="flex items-center gap-2 mb-4">
                <SportBadge sport="cycling" />
                <h3 className="card-label">Cycling performance</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="FTP" unit="watts">
                  <input
                    type="number"
                    min={50}
                    max={600}
                    className={inputCls}
                    value={form.ftp}
                    onChange={(e) => setForm({ ...form, ftp: e.target.value })}
                    data-testid="input-ftp"
                    placeholder="e.g. 250"
                  />
                </Field>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection
            title="Timezone"
            description="Choose the timezone used for your training calendar, daily coaching, and workout matching."
            testId="timezone-form"
          >
            <div className="space-y-5 min-w-0">
              <div className="min-w-0">
                <div className="card-label mb-2">Selected timezone</div>
                <TimezoneSelect
                  value={tzInput}
                  onChange={setTzInput}
                  placeholder="Search city or timezone..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="timezone-status">
                <MetaBlock label="Current saved timezone" testId="current-timezone">
                  {tzProfile?.timezone ? (
                    <>
                      <div>{formatPrimaryLabel(tzProfile.timezone)}</div>
                      <div className="font-mono text-[11px] text-dark-text-muted mt-1 break-words">
                        {tzProfile.timezone}
                      </div>
                    </>
                  ) : (
                    "Not set"
                  )}
                </MetaBlock>
                {detectedTz ? (
                  <MetaBlock label="Detected on this device" testId="browser-detected-timezone">
                    <div>{formatPrimaryLabel(detectedTz)}</div>
                    <div className="font-mono text-[11px] text-dark-text-muted mt-1 break-words">{detectedTz}</div>
                    {detectedTz !== canonicalizeIana(tzProfile?.timezone || "") ? (
                      <button
                        type="button"
                        className="mt-2 text-xs text-brand-primary hover:underline"
                        onClick={() => setTzInput(detectedTz)}
                        data-testid="use-detected-timezone"
                      >
                        Use detected timezone
                      </button>
                    ) : null}
                  </MetaBlock>
                ) : null}
              </div>

              <button
                type="button"
                onClick={saveTimezone}
                disabled={tzSaving || !canonicalizeIana((tzInput || "").trim())}
                className={saveBtnCls}
                data-testid="save-timezone-button"
              >
                {tzSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Saving timezone…
                  </>
                ) : (
                  "Save Timezone"
                )}
              </button>
            </div>
          </SettingsSection>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <CoachingAutonomySettings />
        <CoachingMemorySettings />
      </div>
    </div>
  );
};
